<script lang="ts">
    import type {Alphabet, AlphabetLetter} from './types';
    import type {Identified} from 'typings/portaro.be.types';
    import {inline, prop} from 'shared/utils/pipes';
    import {pipe} from 'core/utils';
    import {exists, isUndefined} from 'shared/utils/custom-utils';
    import {createEventDispatcher} from 'svelte';
    import {A_Z_SORTING_ID, ALPHABETS, ALPHABETS_WITH_DIACRITICAL_LETTERS} from './constants';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import KpDropdownButton from 'shared/ui-widgets/dropdown/KpDropdownButton.svelte';
    import KpDropdownMenuItem from 'shared/ui-widgets/dropdown/KpDropdownMenuItem.svelte';

    export let searchFeatures: string[] = [];
    export let fonds: Identified<number>[] | undefined;
    export let prefix: string | undefined;
    export let diacriticalPrefix: string | undefined;

    const dispatch = createEventDispatcher<{
        'prefix-click': {
            letter: AlphabetLetter,
            diacritical: boolean
        }
    }>();

    $: availableAlphabets = getAvailableAlphabets(searchFeatures)

    function getAvailableAlphabets(features: string[]): Alphabet[] {
        if (!features) {
            return [];
        }
        return ALPHABETS.filter((alphabet) => {
            if (alphabet.id === 'start' && features.includes('alphabet-start')) {
                return true;
            }
            if (alphabet.id === 'numbers' && features.includes('alphabet-numbers')) {
                return true;
            }
            if (alphabet.id === 'latin' && features.includes('alphabet-latin')) {
                return true;
            }
            return alphabet.id === 'cyrillic' && features.includes('alphabet-cyrillic');
        });
    }

    function getAllDiacriticalVariantsJoined(currentPrefix: string, currentDiacriticalPrefix: string): string {
        const alphabetContainingCurrentPrefix = ALPHABETS.find((alphabet) => alphabet.value.find((letter) => letter.value === currentPrefix));
        const currentPrefixLetter = (alphabetContainingCurrentPrefix ?? {value: [] as AlphabetLetter[]}).value.find((letter) => letter.value === currentPrefix);

        if (!exists(currentPrefixLetter) || !exists(currentPrefixLetter.diacriticalLetters)) {
            return currentPrefix ?? currentDiacriticalPrefix;
        }

        if (currentPrefixLetter.value === currentDiacriticalPrefix) {
            return currentDiacriticalPrefix;
        }

        return [currentPrefix, ...currentPrefixLetter.diacriticalLetters.map((letter) => letter.value)].join(', ');
    }

    function isLetterOrDiacriticalVariantActive(currentPrefix: string, currentDiacriticalPrefix: string, letter: AlphabetLetter): boolean {
        if (letter.id === A_Z_SORTING_ID) {
            return isUndefined(currentPrefix);
        }

        return letter.value === currentPrefix
            || letter.value === currentDiacriticalPrefix
            || exists((letter.diacriticalLetters ?? []).find((l) => l.value === currentDiacriticalPrefix));
    }

    const handleLetterClick = (letter: AlphabetLetter, diacritical: boolean) => {
        dispatch('prefix-click', {
            letter,
            diacritical
        });
    }
</script>

{#if availableAlphabets.length > 0}
    <div class="kp-search-alphabets-display">
        {#each availableAlphabets as alphabet(alphabet.id)}
            {@const diacriticalAlphabet = ALPHABETS_WITH_DIACRITICAL_LETTERS.includes(alphabet.id)}

            <ul class="alphabet alphabet-{alphabet.id} {pipe(fonds, prop('id'),  inline(' ', 'fond_'))}">
                {#each alphabet.value as letter(letter.id)}
                    {@const hasDiacriticalLetterVariants = exists(letter.diacriticalLetters) && letter.diacriticalLetters.length > 0}
                    {@const isAzSortingLetter = letter.id === A_Z_SORTING_ID}
                    {@const letterActive = isLetterOrDiacriticalVariantActive(prefix, diacriticalPrefix, letter)}

                    <li class="letter letter-{letter.id}"
                        class:az-sorting={isAzSortingLetter}
                        class:active={letterActive}>

                        <button class="exact-letter-button unset-style"
                                class:full-height={!hasDiacriticalLetterVariants}
                                class:az-storing={isAzSortingLetter}
                                class:vertically-centered={!diacriticalAlphabet}
                                on:click={() => handleLetterClick(letter, false)}>

                            {#if isAzSortingLetter}
                                <UIcon icon="sort-alpha-down" color="var(--accent-blue-new)"/>
                            {:else}
                                {letter.text}
                            {/if}
                        </button>

                        {#if hasDiacriticalLetterVariants}
                            <KpDropdownButton useBootstrapStyles="{false}"
                                              additionalContainerClasses="diacritical-letter-variants-button-container"
                                              additionalButtonClasses="diacritical-letter-variants-button"
                                              additionalMenuClasses="diacritical-letter-variants-button-menu">

                                <svelte:fragment slot="button">
                                    <UIcon icon="angle-small-down"/>
                                </svelte:fragment>

                                <svelte:fragment slot="menu">
                                    <KpDropdownMenuItem on:click={() => handleLetterClick(letter, true)}>
                                        {letter.text}
                                    </KpDropdownMenuItem>

                                    {#each letter.diacriticalLetters as diacriticalLetter(diacriticalLetter.id)}
                                        <KpDropdownMenuItem on:click={() => handleLetterClick(diacriticalLetter, true)}>
                                            {diacriticalLetter.text}
                                        </KpDropdownMenuItem>
                                    {/each}
                                </svelte:fragment>
                            </KpDropdownButton>
                        {/if}
                    </li>
                {/each}
            </ul>
        {/each}
    </div>

    {#if exists(prefix) || exists(diacriticalPrefix)}
        <div class="current-alphabet-index">
            {getAllDiacriticalVariantsJoined(prefix, diacriticalPrefix)}
        </div>
    {/if}
{/if}

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "bootstrap-less/bootstrap/variables";

    @highlight-color: #F5F5F5;
    @active-color: #F4E1DC;

    @letter-width: 24px;
    @letter-height: 38px;

    .kp-search-alphabets-display {
        display: flex;
        flex-wrap: wrap;
        row-gap: @spacing-sm;
        column-gap: @spacing-ml;
        margin-top: @spacing-xl;
        margin-bottom: @spacing-m;

        .alphabet {
            display: flex;
            flex-wrap: wrap;
            gap: @spacing-xs;

            .letter {
                height: @letter-height;
                min-width: @letter-width;
                display: flex;
                flex-direction: column;
                align-items: center;
                border-radius: @border-radius-small;
                border: 1px solid @panel-default-border;
                transition: border-color 0.2s ease-in-out, background-color 0.2s ease-in-out;

                &.active {
                    border-color: var(--brand-orange-new);
                    background-color: @active-color;
                }

                &.az-sorting {
                    width: @letter-height;

                    .exact-letter-button {
                        font-size: @font-size-large;
                    }
                }

                .exact-letter-button {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    outline: none;
                    border: none;
                    background: none;
                    text-decoration: none;
                    cursor: pointer;
                    padding: 0;
                    border-radius: @border-radius-small;

                    &:hover {
                        background-color: @highlight-color;
                    }

                    &.full-height {
                        flex: 1;
                    }

                    &:not(.vertically-centered) {
                        align-items: start;
                        padding-top: 1px;
                    }
                }

                .diacritical-letter-variants-button {
                    flex: 1 1 0;
                    font-size: @font-size-small;
                    border-top-left-radius: 0;
                    border-top-right-radius: 0;
                    border-top: 1px solid @panel-default-border;
                }
            }
        }
    }

    .current-alphabet-index {
        font-size: 2em;
        border: 1px solid @panel-default-border;
        background-color: @highlight-color;
        border-radius: @border-radius-small;
        padding: @spacing-s @spacing-ml;
        margin: @spacing-s 0 @spacing-m;
        display: inline-block;
    }

    :global {
        .kp-search-alphabets-display {
            .diacritical-letter-variants-button-container {
                flex: 1 1 0;
                width: 100%;
            }

            .diacritical-letter-variants-button {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                outline: none;
                border: none;
                background: none;
                text-decoration: none;
                cursor: pointer;
                padding: 0;
                font-size: @font-size-small;
                border-bottom-left-radius: @border-radius-small;
                border-bottom-right-radius: @border-radius-small;
                border-top: 1px solid @panel-default-border;

                &:hover {
                    background-color: @highlight-color;
                }
            }

            .diacritical-letter-variants-button-menu {
                min-width: auto;
            }
        }
    }
</style>